import YAML from 'js-yaml';
import { isFieldChange, type FieldChange } from '../types';

// Config/argument keys the code-test plan pipeline treats as editable params.
// Mirrors the backend `_apply_config_params_from_change` allow-list.
const CONFIG_PARAM_KEYS = ['where', 'limit', 'store_failures'] as const;

const STRUCTURAL_KEYS = new Set([
  'model',
  'column_name',
  'name',
  'description',
  'tags',
  'meta',
  'owner',
  'owners',
]);

const mergeInto = (target: Record<string, unknown>, source: unknown): void => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return;
  for (const [key, value] of Object.entries(
    source as Record<string, unknown>,
  )) {
    if (key === 'config' || key === 'arguments' || key === 'meta') continue;
    if (STRUCTURAL_KEYS.has(key)) continue;
    target[key] = value;
  }
};

export const parseCodeTestConfig = (
  yamlStr?: string | null,
): Record<string, unknown> => {
  if (typeof yamlStr !== 'string' || !yamlStr.trim()) return {};
  let parsed: unknown;
  try {
    parsed = YAML.load(yamlStr);
  } catch {
    return {};
  }
  const entry = Array.isArray(parsed) ? parsed[0] : parsed;
  if (!entry || typeof entry !== 'object') return {};
  const entryObj = entry as Record<string, unknown>;
  const testName = Object.keys(entryObj)[0];
  if (!testName) return {};
  const body = entryObj[testName];
  if (!body || typeof body !== 'object') return {};
  const bodyObj = body as Record<string, unknown>;

  const flat: Record<string, unknown> = {};
  mergeInto(flat, bodyObj);
  mergeInto(flat, bodyObj.arguments);
  mergeInto(flat, bodyObj.config);
  return flat;
};

export const toFieldChange = (value: unknown): FieldChange<unknown> => {
  if (isFieldChange(value)) {
    return value;
  }
  return { current: null, new: value ?? null };
};

export const normalizeCodeTestParams = (
  params?: Record<string, unknown> | null,
): Record<string, FieldChange<unknown>> | undefined => {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, toFieldChange(value)]),
  );
};

// The change's `yaml` can arrive as a FieldChange or, in raw agent tool args,
// as a bare YAML string (new-only). Normalize both to { current, new }.
const normalizeYamlFieldChange = (
  yaml: FieldChange<string | null> | string | null | undefined,
): FieldChange<string | null> | undefined => {
  if (yaml == null) return undefined;
  if (typeof yaml === 'string') return { current: null, new: yaml };
  return yaml;
};

// Only keys whose value actually changed are returned, so preview enrichment
// (yaml.current === yaml.new) yields nothing.
export const deriveParamsFromYaml = (
  yaml: FieldChange<string | null> | string | null | undefined,
): Record<string, FieldChange<unknown>> => {
  const normalized = normalizeYamlFieldChange(yaml);
  if (!normalized) return {};
  const currentConfig = parseCodeTestConfig(normalized.current);
  const newConfig = parseCodeTestConfig(normalized.new);

  const result: Record<string, FieldChange<unknown>> = {};
  for (const key of CONFIG_PARAM_KEYS) {
    const hasCurrent = key in currentConfig;
    const hasNew = key in newConfig;
    if (!hasCurrent && !hasNew) continue;
    const currentValue = hasCurrent ? currentConfig[key] : null;
    const newValue = hasNew ? newConfig[key] : null;
    if (JSON.stringify(currentValue) === JSON.stringify(newValue)) continue;
    result[key] = { current: currentValue ?? null, new: newValue ?? null };
  }
  return result;
};
